import { useEffect, useState } from "react";
import { X, AlertTriangle, Rocket } from "lucide-react";
import { ChannelOption } from "@agent/components/ChannelOption";
import { computeChannelOptions } from "@agent/lib/publishModalOptions";

export function PublishModal({ open, onClose, onConfirm, draft, publishing, linkedinReady, liConnected, liHasOrgId, websiteReady, blogGenerated, websiteHost }) {
  const [ack, setAck] = useState(false);
  const [selection, setSelection] = useState("both"); // "linkedin" | "blog" | "both"

  const { options, liAvailable, blogAvailable } = computeChannelOptions({ draft, linkedinReady, liConnected, liHasOrgId, websiteReady, blogGenerated, websiteHost });

  useEffect(() => {
    if (!open) return;
    // Default: pick the widest available option
    if (liAvailable && blogAvailable) setSelection("both");
    else if (liAvailable) setSelection("linkedin");
    else if (blogAvailable) setSelection("blog");
    else setSelection("both");
    setAck(false);
    // `setAck`/`setSelection` are stable useState setters and don't need to be tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, liAvailable, blogAvailable]);

  if (!open) return null;

  const channelsForSelection = selection === "both" ? ["linkedin", "blog"] : [selection];
  const canPublish = channelsForSelection.length > 0 && !options.find((o) => o.id === selection)?.disabled && ack && !publishing;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="publish-modal">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-lg w-full fade-up overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-display font-bold text-[#0f172a] flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center"><AlertTriangle size={15} className="text-rose-500" /></span>
            Publish gate
          </h3>
          <button data-testid="publish-modal-close" onClick={onClose} className="text-slate-400 hover:text-[#0f172a] transition-colors duration-150"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-500 leading-relaxed">
            Publishing <span className="text-[#0f172a] font-semibold">"{draft?.title}"</span>. Pick where it goes — this posts publicly and cannot be undone from here.
          </p>

          <div className="space-y-2" role="radiogroup" aria-label="Publish channel">
            {options.map((opt) => (
              <ChannelOption key={opt.id} {...opt} active={selection === opt.id} onSelect={setSelection} />
            ))}
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs space-y-1.5">
            <p className="text-slate-500">LinkedIn post: <span className="text-[#0f172a] font-semibold">{draft?.content?.length || 0}/3000 chars{draft?.image_ids?.length ? ` · ${draft.image_ids.length} image${draft.image_ids.length > 1 ? "s" : ""}` : ""}</span></p>
            <p className="text-slate-500">Blog article: <span className="text-[#0f172a] font-semibold">{blogGenerated ? `"${draft?.blog_title}" · ${draft?.blog_markdown?.length || 0} chars markdown` : "not generated"}</span></p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer group">
            <input data-testid="publish-ack-checkbox" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-rose-500" />
            <span className="text-xs text-slate-500 group-hover:text-[#0f172a] transition-colors duration-150 leading-relaxed">
              I reviewed this content and explicitly approve publishing it to the selected channels.
            </span>
          </label>
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button data-testid="publish-cancel-btn" onClick={onClose}
            className="flex-1 py-3 rounded-full border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors duration-150">
            Cancel
          </button>
          <button data-testid="publish-confirm-btn" disabled={!canPublish}
            onClick={() => onConfirm(channelsForSelection)}
            className="flex-1 py-3 rounded-full bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            <Rocket size={14} /> {publishing ? "Publishing…" : selection === "both" ? "Publish to both" : selection === "linkedin" ? "Publish to LinkedIn" : "Publish to blog"}
          </button>
        </div>
      </div>
    </div>
  );
}
